import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Button } from '@mdvip/shared/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@mdvip/shared/components/ui/dialog';
import { Sheet, SheetContent, SheetTitle } from '@mdvip/shared/components/ui/sheet';
import { useCurrency } from '@mdvip/shared/hooks/useCurrency';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { useFormatDate } from '@mdvip/shared/hooks/useFormatDate';
import { useIsMobile } from '@mdvip/shared/hooks/useIsMobile';
import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { UpcomingPayment } from '@/hooks/payments/upcomingPayments/upcomingPaymentsApi';
import { useCancelUpcomingPayment } from '@/hooks/payments/upcomingPayments/useCancelUpcomingPayment';
import { AccountCell } from './AccountCell';

interface CancelPaymentDialogProps {
  /** The payment pending cancellation; `null` closes the dialog. Controlled like `RemoveCardDialog`. */
  payment: UpcomingPayment | null;
  onClose: () => void;
  /** Called once the mutation succeeds — the caller owns the success banner copy. */
  onSuccess: () => void;
}

/** Read-only detail styled as a filled field, label above value — the design's `ReadField`. */
function ReadField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-muted px-3.5 py-2.5 text-left">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-foreground">{children}</div>
    </div>
  );
}

/**
 * Confirms cancelling one scheduled payment.
 *
 * Follows the `Modal-CancelPayment` handoff: left-aligned title with a close affordance, three
 * read-only fields stacked as filled cards, and a stacked button pair with the destructive confirm
 * first. No icon medallion and no description paragraph — the design has neither.
 *
 * Two deviations, both mandated by the ticket over the mockup: the shell is this repo's
 * `Dialog`(desktop)/`Sheet`(mobile) swap rather than the design system's `BottomSheet`, whose
 * `DialogContent variant="sheet"` does not exist here; and the Account value renders through
 * `AccountCell` rather than a plain string, matching the table one row up.
 */
export function CancelPaymentDialog({ payment, onClose, onSuccess }: CancelPaymentDialogProps) {
  const { t } = useTranslation('members');
  const tError = useErrorTranslation();
  const isMobile = useIsMobile();
  const { formatCurrency } = useCurrency();
  const { formatDateOnly } = useFormatDate();
  const { submit, isPending, isError, reset } = useCancelUpcomingPayment({ onSuccess });

  // The dialog stays mounted between opens, so mutation state outlives a close. Without this a
  // failure on one row would greet the member with a stale error when they open a different row.
  const paymentId = payment?.id;
  useEffect(() => {
    reset();
  }, [paymentId, reset]);

  const handleOpenChange = (open: boolean) => {
    if (!open && !isPending) onClose();
  };

  const handleConfirm = () => {
    if (payment) submit(payment.id);
  };

  const title = t('payments.upcomingPayments.cancelDialog.title');
  const TitleTag = isMobile ? SheetTitle : DialogTitle;
  const titleNode = <TitleTag className="min-w-0 truncate text-xl font-semibold text-foreground">{title}</TitleTag>;

  const body = payment && (
    <>
      <div className="flex flex-col gap-2.5">
        <ReadField label={t('payments.upcomingPayments.cancelDialog.fieldDate')}>
          <span className="tabular-nums">{formatDateOnly(payment.scheduledDate)}</span>
        </ReadField>
        <ReadField label={t('payments.upcomingPayments.cancelDialog.fieldAmount')}>
          {/* Salesforce debits the balance, so show the magnitude, matching the table. */}
          <span className="tabular-nums">{formatCurrency(Math.abs(payment.amount))}</span>
        </ReadField>
        <ReadField label={t('payments.upcomingPayments.cancelDialog.fieldAccount')}>
          <AccountCell account={payment} />
        </ReadField>
      </div>

      {isError && (
        <Alert variant="destructive" className="mt-4 text-left">
          <Icon name="alertTriangle" weight="solid" />
          <AlertTitle>{t('payments.upcomingPayments.cancelDialog.errorTitle')}</AlertTitle>
          <AlertDescription>{tError('UPCOMING_PAYMENT_CANCEL_FAILED')}</AlertDescription>
        </Alert>
      )}
    </>
  );

  const confirmContent = isPending ? (
    <>
      <Icon name="spinner" weight="solid" className="animate-spin" />
      {t('payments.upcomingPayments.cancelDialog.submitting')}
    </>
  ) : (
    t(isError ? 'payments.upcomingPayments.cancelDialog.retry' : 'payments.upcomingPayments.cancelDialog.confirm')
  );

  /* Stacked, full-width, destructive first, divided from the body — per the handoff. */
  const actions = (
    <div className="-mx-6 mt-4 flex flex-col gap-2 border-t border-border px-6 pt-4 [&>button]:w-full">
      <Button variant="destructive" disabled={isPending} onClick={handleConfirm}>
        {confirmContent}
      </Button>
      <Button variant="outline" onClick={onClose} disabled={isPending}>
        {t('payments.upcomingPayments.cancelDialog.dismiss')}
      </Button>
    </div>
  );

  const closeButton = (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={onClose}
      disabled={isPending}
      aria-label={t('payments.upcomingPayments.cancelDialog.close')}
      className="-mr-1 shrink-0 rounded-full text-muted-foreground"
    >
      <Icon name="x" size="sm" />
    </Button>
  );

  const header = (
    <div className="flex items-center justify-between gap-3 border-b border-border px-6 py-3.5">
      {titleNode}
      {closeButton}
    </div>
  );

  const content = (
    <div className="px-6 py-4">
      {body}
      {actions}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={payment !== null} onOpenChange={handleOpenChange}>
        <SheetContent side="bottom" showCloseButton={false} className="gap-0 p-0 pb-[env(safe-area-inset-bottom)]">
          {header}
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={payment !== null} onOpenChange={handleOpenChange}>
      {/* gap-0 p-0 so the header and footer rules span the full panel width; the shell's own
          p-6/gap-6 would inset them. Width is the shell's default sm:max-w-md, not narrowed. */}
      <DialogContent showCloseButton={false} className="gap-0 p-0">
        {header}
        {content}
      </DialogContent>
    </Dialog>
  );
}
